import { API_URL } from "../../shared/constants";
import { emptySplitApi } from "../emptySplit-api";

// Ownership shape resolved by the server (TSK-OO-003). The object mirrors the
// `resolveOwnership` result and is attached to the order item's `ownership`
// field on the get endpoint. OrderInfo consumes this so both viewers stay typed.
export interface OrderOwnershipUser {
  _id: string | null;
  name: string | null;
  username: string | null;
}

export interface OrderOwnership {
  createdBy: OrderOwnershipUser | null;
  createdAt: string | null;
  lastModifiedBy: OrderOwnershipUser | null;
  updatedAt: string | null;
  isModified: boolean;
}

export const orderApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    orderList: builder.query<any, any>({
      query: (data: any) => {
        return {
          url: `/${API_URL.ORDERS}/list`,
          method: "post",
          body: data,
        };
      },
    }),
    orderGet: builder.query({
      query: (id) => ({
        url: `/${API_URL.ORDERS}/get/${id}`,
        method: "post",
      }),
    }),
    orderUpdate: builder.mutation<any, any>({
      query: ({ id, data }) => {
        return {
          url: `/${API_URL.ORDERS}/update/${id}`,
          method: "post",
          body: data,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "AuditTrail" as const, id },
      ],
    }),
    orderConfirm: builder.mutation<any, any>({
      query: (id) => {
        return {
          url: `/${API_URL.ORDERS}/confirm/${id}`,
          method: "post",
        };
      },
      invalidatesTags: (result, error, id) => [
        { type: "AuditTrail" as const, id },
      ],
    }),
    orderCancel: builder.mutation<any, any>({
      query: ({ id, data }) => {
        return {
          url: `/${API_URL.ORDERS}/cancel/${id}`,
          method: "post",
          body: data,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "AuditTrail" as const, id },
      ],
    }),
    printOrder: builder.mutation<any, any>({
      query: ({ id, data }) => {
        return {
          url: `/${API_URL.ORDERS}/print-counter/${id}`,
          method: "post",
          body: data,
        };
      },
    }),
    publicOrderGet: builder.query({
      query: (id) => ({
        url: `/public/order/get/${id}`,
        method: "post",
      }),
    }),
    ordersExport: builder.mutation<any, any>({
      query: (data) => {
        return {
          url: `/${API_URL.ORDERS}/export`,
          method: "post",
          body: data,
        };
      },
    }),
    // Image endpoints
    imageUpload: builder.mutation<any, FormData>({
      query: (data) => {
        return {
          url: `/${API_URL.IMAGES}/upload`,
          method: "post",
          body: data,
        };
      },
    }),
    imageDelete: builder.mutation<any, string>({
      query: (imageId) => {
        return {
          url: `/${API_URL.IMAGES}/delete/${imageId}`,
          method: "delete",
        };
      },
    }),
    imageDownloadAll: builder.mutation<any, string>({
      query: (orderId) => {
        return {
          url: `/${API_URL.IMAGES}/download-all/${orderId}`,
          method: "post",
        };
      },
    }),
    // Order Communication (Epic 3) — comment endpoints
    orderCommentsList: builder.query<any, any>({
      query: (data) => ({
        url: `/${API_URL.ORDERS}/comments/list`,
        method: "post",
        body: data,
      }),
    }),
    orderCommentCreate: builder.mutation<any, FormData>({
      query: (data) => ({
        url: `/${API_URL.ORDERS}/comments/create`,
        method: "post",
        body: data,
      }),
    }),
    orderCommentImportant: builder.mutation<any, string>({
      query: (commentId) => ({
        url: `/${API_URL.ORDERS}/comments/important/${commentId}`,
        method: "post",
      }),
    }),
    orderAdminUpdate: builder.mutation<any, any>({
      query: ({ id, data }) => ({
        url: `/${API_URL.ORDERS}/admin-update/${id}`,
        method: "post",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AuditTrail" as const, id },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useOrderListQuery,
  useOrderGetQuery,
  useOrderUpdateMutation,
  useOrderConfirmMutation,
  useOrderCancelMutation,
  usePrintOrderMutation,
  usePublicOrderGetQuery,
  useOrdersExportMutation,
  useImageUploadMutation,
  useImageDeleteMutation,
  useImageDownloadAllMutation,
  useOrderCommentsListQuery,
  useLazyOrderCommentsListQuery,
  useOrderCommentCreateMutation,
  useOrderCommentImportantMutation,
  useOrderAdminUpdateMutation,
} = orderApi;
